import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, useConfirm } from '@pletly/ui';

import { getName } from '@pletly/shared';
import { TeamMemberDataType } from './TeamMembersTable';
import { useRemoveMemberFromTeamMutation } from './__generated__/removeMemberFromTeam.generated';
import { UserRole } from '../../../../__generated__/types';

export const useRemoveMemberFromTeam = (teamUuid: string) => {
	const { t } = useTranslation();
	const [removeTeamMember] = useRemoveMemberFromTeamMutation();
	const { confirm } = useConfirm();

	const removeMemberFromTeam = useCallback(
		(memberData: TeamMemberDataType) => async () => {
			const shouldRemove = await confirm(
				t('confirmations.remove-from-team.title', { name: getName(memberData) }),
				memberData.role === UserRole.User
					? t('confirmations.remove-from-team.content-care-recipient')
					: t('confirmations.remove-from-team.content-caregiver'),
			);
			if (!shouldRemove) return;

			try {
				await removeTeamMember({
					variables: {
						data: {
							memberId: memberData.memberId,
							teamUuid,
						},
					},
					update: (cache, { data }) => {
						if (!data) return;
						const teamMemberRef = cache.identify({
							__typename: 'OrganisationTeamMember',
							id: data.removeMemberFromTeam,
						});
						cache.evict({ id: teamMemberRef });
					},
				});
				Toast.show({
					type: 'success',
					text1: t('toasts.success.member-removed-from-team'),
				});
			} catch {
				Toast.show({
					type: 'error',
					text1: t('toasts.error.something-wrong'),
				});
			}
		},
		[removeTeamMember, t, teamUuid, confirm],
	);

	return {
		removeMemberFromTeam,
	};
};
